import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { dateBadge, sportName } from "@/lib/helpers";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Eye, Send, CreditCard, Star, Receipt, X, RefreshCw } from "lucide-react";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  payment_pending: { label: "Payment Pending", cls: "bg-amber-100 text-amber-700" },
  pending_approval: { label: "Pending Approval", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  published: { label: "Published", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  expired: { label: "Listing Expired", cls: "bg-slate-200 text-slate-600" },
};
const PAY_META = {
  unpaid: { label: "Unpaid", cls: "text-slate-500" }, pending: { label: "Pending", cls: "text-amber-600" },
  paid: { label: "Paid", cls: "text-emerald-600" }, failed: { label: "Failed", cls: "text-red-600" },
};
const TABS = [["all", "All"], ["draft", "Drafts"], ["pending_approval", "Pending"], ["published", "Published"], ["expired", "Expired"], ["rejected", "Rejected"]];
const cy = (c) => (c === "MYR" ? "RM" : (c || "RM") + " ");

export default function OrganizerDashboard() {
  const [races, setRaces] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [pricing, setPricing] = useState(null);
  const [modal, setModal] = useState(null); // {type, race, months, items, total}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  // Fetch organizer races. An API/DB failure must NEVER be shown as an empty list,
  // and must never wipe out data already on screen. We auto-retry a few times, then
  // surface an explicit error state with a Retry button.
  const load = useCallback(async (attempt = 0) => {
    if (attempt === 0) { setLoading(true); setError(false); }
    try {
      const { data } = await api.get("/organizer/races");
      setRaces(data.races || []);
      setOrg(data.organizer);
      setError(false);
      setLoading(false);
    } catch (e) {
      if (attempt < 2) {
        setTimeout(() => load(attempt + 1), 1200 * (attempt + 1));
      } else {
        setError(true);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/organizer/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, [load]);

  const currency = pricing?.currency || "MYR";
  const featuredDurations = pricing?.featured_durations || [1, 3];

  // Compute what the organizer owes for a race (ONE combined checkout).
  const owedFor = (r) => {
    const items = [];
    if (r.payment_status !== "paid") items.push({ label: "Race Listing", duration: `${pricing?.listing_duration_months || 12} Months`, amount: pricing?.listing_price });
    if (r.featured_status === "approved") {
      const m = r.featured_approved_months || r.featured_months || featuredDurations[0];
      items.push({ label: "Featured Race", duration: m === 1 ? "1 Month" : `${m} Months`, amount: (pricing?.featured_prices || {})[String(m)] });
    }
    return items;
  };

  const openCheckout = (r) => {
    const items = owedFor(r);
    const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    setModal({ type: "checkout", race: r, items, total });
  };
  const openFeatureRequest = (r) => setModal({ type: "feature-request", race: r, months: featuredDurations[0] });

  const confirmCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/checkout?race_id=${modal.race.id}`);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };
  const confirmFeatureRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/organizer/races/${modal.race.id}/feature/request?months=${modal.months}`);
      toast.success("Feature Race request submitted — pending admin approval");
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request"); }
    finally { setBusy(false); }
  };

  const submit = async (id) => {
    try { await api.post(`/organizer/races/${id}/submit`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this race?")) return;
    await api.delete(`/organizer/races/${id}`); toast.success("Deleted"); load();
  };

  const filtered = tab === "all" ? races : races.filter((r) => r.status === tab);
  const counts = races.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const slotsFull = pricing?.featured_slots_full;
  const showErr = error && races.length === 0;

  return (
    <Layout>
      <div className="relative bg-[#0a1420] overflow-hidden">
        <img src="https://images.pexels.com/photos/29840338/pexels-photo-29840338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="font-display font-semibold text-[#22d3ee] tracking-widest text-xs uppercase">Organizer</span>
            <h1 className="font-display font-black uppercase text-4xl text-white">{org?.organization_name || "Dashboard"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/organizer/billing" data-testid="billing-link" className="inline-flex items-center gap-2 bg-white/10 text-white font-bold px-4 py-3 rounded-lg hover:bg-white/20 transition-colors"><Receipt size={18} /> Invoices</Link>
            <Link to="/organizer/races/new" data-testid="create-race-button" className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a1420] font-bold px-6 py-3 rounded-lg hover:bg-white transition-colors"><Plus size={18} /> Create New Race</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showErr && <>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          {[["Total", races.length], ["Drafts", counts.draft || 0], ["Pending", counts.pending_approval || 0], ["Published", counts.published || 0], ["Rejected", counts.rejected || 0]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl p-4"><div className="font-display font-black text-3xl text-slate-800">{v}</div><div className="text-sm text-slate-500">{l}</div></div>
          ))}
        </div>

        <div className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto no-scrollbar">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`org-tab-${v}`}
              className={`px-4 py-2.5 font-semibold text-sm whitespace-nowrap border-b-2 ${tab === v ? "border-[#22d3ee] text-[#0a1420]" : "border-transparent text-slate-500"}`}>{l}</button>
          ))}
        </div>
        </>}

        {loading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div> :
          showErr ? (
            <div className="py-16 text-center" data-testid="org-races-error">
              <p className="text-slate-800 font-display font-bold text-lg mb-1">Unable to load your races.</p>
              <p className="text-slate-500 text-sm mb-5">Your races are safe — this is a temporary problem loading them. Please try again.</p>
              <button onClick={() => load(0)} data-testid="retry-races-button" className="inline-flex items-center gap-2 bg-[#0a1420] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#1a2740] transition-colors"><RefreshCw size={16} /> Try again</button>
            </div>
          ) :
          filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No races here yet. <Link to="/organizer/races/new" className="text-[#06b6d4] font-semibold">Create your first race →</Link></div>
          ) : (
            <div className="space-y-3" data-testid="organizer-races-list">
              {filtered.map((r) => {
                const b = dateBadge(r.date);
                const sm = STATUS_META[r.status] || STATUS_META.draft;
                const pm = PAY_META[r.payment_status] || PAY_META.unpaid;
                const owed = owedFor(r);
                const canFeature = pricing?.featured_enabled && !r.featured && !["pending", "approved", "active"].includes(r.featured_status) && ["published", "approved"].includes(r.status);
                return (
                  <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`org-race-${r.id}`}>
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 shrink-0">{r.hero_image ? <img src={r.hero_image} alt="" className="w-full h-full object-cover" /> : <div className="grid place-items-center h-full text-slate-300 text-xs">No image</div>}</div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-display font-bold uppercase text-slate-800">{r.name}</div>
                      <div className="text-sm text-slate-500">{sportName(r.sport)} · {b.day} {b.month} {b.year} · {r.city}</div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${sm.cls}`}>{sm.label}</span>
                        <span className={`text-xs font-semibold ${pm.cls}`}>Payment: {pm.label}</span>
                        {r.payment_status === "paid" && r.listing_expiry && <span className="text-xs text-slate-400">Listing until {(r.listing_expiry || "").slice(0, 10)}</span>}
                      </div>
                      {r.status === "rejected" && r.rejection_reason && <p className="text-xs text-red-600 mt-1">Reason: {r.rejection_reason}</p>}
                      {r.featured_status === "pending" && <p className="text-xs text-blue-600 font-semibold mt-1 inline-flex items-center gap-1"><Star size={12} /> Feature Race request — pending admin approval</p>}
                      {r.featured_status === "approved" && <p className="text-xs text-amber-600 font-semibold mt-1 inline-flex items-center gap-1"><Star size={12} /> Feature Race approved — included in your next payment</p>}
                      {r.featured_status === "rejected" && <p className="text-xs text-slate-500 mt-1 inline-flex items-start gap-1"><Star size={12} className="mt-0.5" /> Feature Race request declined{r.featured_reject_reason ? `: ${r.featured_reject_reason}` : ""}</p>}
                      {r.featured && r.featured_status === "active" && <p className="text-xs text-emerald-600 font-semibold mt-1 inline-flex items-center gap-1" data-testid={`featured-active-${r.id}`}><Star size={12} /> Feature Race active{r.featured_end ? ` — until ${(r.featured_end || "").slice(0, 10)}` : ""}</p>}
                      {r.featured_status === "expired" && <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1" data-testid={`featured-expired-${r.id}`}><Star size={12} /> Feature Race expired</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {r.status === "published" && <Link to={`/race/${r.slug}`} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100" title="View"><Eye size={16} /></Link>}
                      <Link to={`/organizer/races/${r.id}/edit`} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100" title="Edit" data-testid={`edit-race-${r.id}`}><Pencil size={16} /></Link>
                      {owed.length > 0 && (
                        <button onClick={() => openCheckout(r)} className="inline-flex items-center gap-1 bg-[#0a1420] text-white text-sm font-bold px-3 py-2 rounded-lg" data-testid={`pay-button-${r.id}`}>
                          <CreditCard size={15} /> Pay{owed.every((i) => i.amount != null) ? ` · ${cy(currency)}${owed.reduce((s, i) => s + Number(i.amount || 0), 0)}` : ""}
                        </button>
                      )}
                      {r.payment_status === "paid" && ["draft", "payment_pending", "rejected"].includes(r.status) && (
                        <button onClick={() => submit(r.id)} className="inline-flex items-center gap-1 bg-[#22d3ee] text-[#0a1420] text-sm font-bold px-3 py-2 rounded-lg" data-testid={`submit-race-${r.id}`}><Send size={15} /> Submit</button>
                      )}
                      {canFeature && (
                        slotsFull ? (
                          <span className="text-xs text-amber-700 font-semibold px-2 py-2 inline-flex items-center gap-1" data-testid={`featured-unavailable-${r.id}`} title="All Feature Race slots are currently occupied"><Star size={13} /> Feature unavailable{pricing.featured_available_from ? ` · from ${pricing.featured_available_from}` : ""}</span>
                        ) : (
                          <button onClick={() => openFeatureRequest(r)} className="inline-flex items-center gap-1 border border-amber-300 text-amber-600 text-sm font-bold px-3 py-2 rounded-lg hover:bg-amber-50" data-testid={`feature-race-${r.id}`}><Star size={15} /> Feature Race</button>
                        )
                      )}
                      <button onClick={() => del(r.id)} className="p-2 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !busy && setModal(null)} data-testid="checkout-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h2 className="font-display font-black uppercase text-xl text-slate-800">
                {modal.type === "feature-request" ? "Request Feature Race" : "Confirm Payment"}
              </h2>
              <button onClick={() => !busy && setModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="close-modal"><X size={20} /></button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-slate-500">Race</span><span className="font-semibold text-slate-800 text-right">{modal.race.name}</span></div>

              {modal.type === "feature-request" ? (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Duration</span>
                  <select value={modal.months} onChange={(e) => setModal({ ...modal, months: Number(e.target.value) })} data-testid="feature-months-select" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                    {featuredDurations.map((m) => <option key={m} value={m}>{m === 1 ? "1 Month" : `${m} Months`}</option>)}
                  </select>
                </div>
              ) : (
                <div className="border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="checkout-line-items">
                  {modal.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5">
                      <div><div className="font-semibold text-slate-800">{it.label}</div><div className="text-xs text-slate-400">{it.duration}</div></div>
                      <div className="font-mono-data font-semibold text-slate-800">{it.amount != null ? `${cy(currency)}${it.amount}` : "—"}</div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-3 bg-slate-50">
                    <span className="font-display font-bold uppercase text-slate-800">Total</span>
                    <span className="font-display font-black text-2xl text-[#0a1420]" data-testid="modal-total">{cy(currency)}{modal.total}</span>
                  </div>
                </div>
              )}
            </div>

            {modal.type === "feature-request" ? (
              <button onClick={confirmFeatureRequest} disabled={busy} className="mt-5 w-full bg-amber-500 text-[#0a1420] font-bold py-3 rounded-lg hover:bg-amber-400 disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="confirm-feature-request">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <Star size={18} />} Submit Request for Approval
              </button>
            ) : (
              <button onClick={confirmCheckout} disabled={busy} className="mt-5 w-full bg-[#0a1420] text-white font-bold py-3 rounded-lg hover:bg-[#1a2740] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="confirm-pay">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Pay with HitPay
              </button>
            )}
            <p className="text-xs text-slate-400 mt-3 text-center">All payments are processed securely in {currency} via HitPay.</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
